// Utility for loading Paddle JS SDK and opening checkout
// No secrets here; uses client-side token from env.

interface PaddleWindow extends Window {
  Paddle?: any;
}

type PaddleCheckoutEventDetail = {
  name: string;
  data: any;
};

declare const window: PaddleWindow;

let paddleLoading: Promise<any> | null = null;

function debugEnabled() {
  return Boolean(import.meta.env.VITE_PADDLE_DEBUG);
}

export function loadPaddle(clientToken: string): Promise<any> {
  // If a promise is already in flight, return it
  if (paddleLoading) {
    return paddleLoading;
  }

  // Guard against legacy Paddle.js (v1) script
  if (window.Paddle && !window.Paddle.Initialize) {
    if (debugEnabled()) {
      console.warn(
        '[Paddle Loader] Legacy Paddle.js (v1) detected. Removing it to load Paddle Billing (v2).'
      );
    }
    const legacyScript = document.querySelector('script[src*="cdn.paddle.com/paddle/paddle.js"]');
    if (legacyScript) {
      legacyScript.remove();
    }
    try {
      // @ts-ignore
      delete window.Paddle;
    } catch (e) {
      // @ts-ignore
      window.Paddle = undefined;
    }
  }

  // Don't load again if we already have Paddle Billing (v2)
  if (window.Paddle && window.Paddle.Initialize) {
    if (debugEnabled()) {
      console.log('[Paddle Loader] Paddle Billing (v2) is already loaded.');
    }
    return Promise.resolve(window.Paddle);
  }

  paddleLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.onload = () => {
      if (debugEnabled()) {
        console.log('[Paddle Loader] Paddle Billing (v2) script loaded successfully.');
      }
      // Environment is inferred from the client token in Paddle v2.
      // Passing an explicit `environment` option to Initialize is invalid and causes an error.

      if (!clientToken) {
        const err = new Error('Paddle client token is not configured.');
        console.error('[Paddle]', err.message);
        paddleLoading = null;
        return reject(err);
      }

      if (!window.Paddle) {
        const err = new Error('window.Paddle is not available after script load.');
        console.error('[Paddle]', err.message);
        paddleLoading = null;
        return reject(err);
      }

      // Force sandbox endpoints when using a sandbox (test_) client token.
      // Per Paddle.js docs, call Paddle.Environment.set('sandbox') BEFORE Initialize().
      // IMPORTANT: The environment is fundamentally inferred from the client token in Paddle v2.
      // To avoid misconfig (e.g. live_ token + VITE_PADDLE_ENV=sandbox), we *never* force
      // sandbox if the token clearly indicates a live environment.
      try {
        const envHint = String(import.meta.env.VITE_PADDLE_ENV || '').toLowerCase();
        const tokenEnv = clientToken.startsWith('test_')
          ? 'sandbox'
          : clientToken.startsWith('live_')
          ? 'live'
          : 'unknown';

        if (envHint && tokenEnv !== 'unknown' && envHint !== tokenEnv && debugEnabled()) {
          console.warn(
            `[Paddle] Ignoring VITE_PADDLE_ENV="${envHint}" because client token implies "${tokenEnv}" environment.`
          );
        }

        const wantsSandbox = tokenEnv === 'sandbox';
        if (wantsSandbox && typeof window.Paddle.Environment?.set === 'function') {
          window.Paddle.Environment.set('sandbox');
          if (debugEnabled()) console.log('[Paddle] Environment.set("sandbox") applied (test_ token).');
        } else if (debugEnabled()) {
          console.log('[Paddle] Using production/live environment (no sandbox override).');
        }
      } catch (e) {
        if (debugEnabled()) console.warn('[Paddle] Failed to set sandbox environment; proceeding with default.', e);
      }

      if (debugEnabled()) {
        console.log('[Paddle] Initializing with client token (env inferred).');
      }

      window.Paddle.Initialize({
        token: clientToken,
        eventCallback: (data: any) => {
          if (debugEnabled()) {
            console.log('[PADDLE EVENT]', data);
          }
          try {
            const eventName = String(data?.name || '');
            if (eventName) {
              window.dispatchEvent(new CustomEvent<PaddleCheckoutEventDetail>('paddle-checkout-event', {
                detail: { name: eventName, data },
              }));
            }
          } catch {}
        },
      });
      resolve(window.Paddle);
      paddleLoading = null; // Reset for future calls if needed
    };
    script.onerror = () => {
      const err = new Error('Failed to load Paddle.js script.');
      console.error('[Paddle Loader]', err.message);
      paddleLoading = null;
      reject(err);
    };
    document.head.appendChild(script);
  });

  return paddleLoading;
}

export interface OpenPaddleCheckoutOptions {
  priceId?: string; // pri_...
  quantity?: number;
  transactionId?: string;
  customerAuthToken?: string;
  savedPaymentMethodId?: string | number;
  variant?: string;
  displayMode?: string;
  frameTarget?: string;
  frameInitialHeight?: string;
  frameStyle?: string;
  userId?: string | number;
  customerId?: string;
  email?: string; // Prefill customer email at checkout
  customData?: Record<string, unknown>;
  onClose?: () => void;
  onSuccess?: (data: any) => void; // success event
  onError?: (err: any) => void;
  autoWaitPaymentId?: string | number | null;
}

export async function openPaddleCheckout(opts: OpenPaddleCheckoutOptions) {
  const token = import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined;
  if (!token) throw new Error('Missing VITE_PADDLE_CLIENT_TOKEN (client token not set)');
  const isTransactionCheckout = Boolean(opts.transactionId);
  const displayMode = opts.displayMode || 'overlay';
  const hasCustomerAuthToken = Boolean(opts.customerAuthToken);

  if (displayMode === 'inline' && !opts.frameTarget) {
    throw new Error('Missing Paddle frameTarget for inline checkout');
  }

  if (isTransactionCheckout) {
    if (!opts.transactionId) throw new Error('Missing Paddle transactionId for transaction checkout');
    if (!opts.customerAuthToken) throw new Error('Missing Paddle customerAuthToken for transaction checkout');
    if (opts.savedPaymentMethodId == null || opts.savedPaymentMethodId === '') {
      throw new Error('Missing Paddle savedPaymentMethodId for transaction checkout');
    }
  } else {
    if (!opts.priceId || !/^pri_/.test(opts.priceId)) {
      throw new Error(`Invalid Paddle priceId: ${opts.priceId}`);
    }
    if (opts.userId == null || opts.userId === '') {
      throw new Error('Missing userId for price-based Paddle checkout');
    }
  }

  // Correctly call loadPaddle with the client token
  const Paddle = await loadPaddle(token);

  try {
    if (debugEnabled()) {
      const masked = token.length > 12 ? `${token.slice(0, 6)}...${token.slice(-4)}` : `${token.slice(0, 3)}...`;
      const inferredEnv = token?.startsWith('test_') ? 'sandbox' : 'live';
      try {
        console.groupCollapsed('[Paddle] Opening checkout');
        console.debug('mode:', isTransactionCheckout ? 'transaction' : 'items');
        if (isTransactionCheckout) {
          console.debug('transactionId:', opts.transactionId);
          console.debug('savedPaymentMethodId:', String(opts.savedPaymentMethodId));
          console.debug('variant:', opts.variant || 'one-page');
        } else {
          console.debug('priceId:', opts.priceId);
          console.debug('userId:', String(opts.userId));
        }
        console.debug('origin:', window?.location?.origin);
        console.debug('referrer:', document?.referrer || '(none)');
        console.debug('referrer meta policy:', (document?.querySelector('meta[name="referrer"]') as HTMLMetaElement | null)?.content || '(none)');
        console.debug('token(masked):', masked);
        console.debug('env (inferred from token):', inferredEnv);
        console.debug('Paddle.Initialize available:', typeof (window as any).Paddle?.Initialize === 'function');
        console.debug('Paddle object keys:', Object.keys((window as any).Paddle || {}));
        console.groupEnd();
      } catch {}
    }
    const currentUrl = window.location.href.split('#')[0];
    const checkoutPayload = isTransactionCheckout
      ? {
          transactionId: opts.transactionId,
          customerAuthToken: opts.customerAuthToken,
          savedPaymentMethodId: String(opts.savedPaymentMethodId),
          settings: {
            displayMode,
            variant: opts.variant || 'one-page',
            allowedPaymentMethods: ['saved_payment_methods'],
            showAddDiscounts: false,
            showAddTaxId: false,
            successUrl: `${currentUrl}#paddle-success`,
            ...(displayMode === 'inline'
              ? {
                  frameTarget: opts.frameTarget,
                  frameInitialHeight: opts.frameInitialHeight || '620',
                  frameStyle: opts.frameStyle || 'width: 100%; min-width: 312px; background-color: transparent; border: none;',
                }
              : {}),
          },
        }
      : {
          items: [{ priceId: opts.priceId, quantity: opts.quantity ?? 1 }],
          // Include both snake_case and camelCase for backend compatibility.
          customData: opts.customData || { user_id: String(opts.userId), userId: String(opts.userId) },
          ...(hasCustomerAuthToken ? { customerAuthToken: opts.customerAuthToken } : {}),
          // Avoid sending both a customer auth token and customer prefill object.
          // Paddle authenticates the customer from the token already, and mixed
          // customer context can lead to opaque checkout failures if they drift.
          ...(!hasCustomerAuthToken
            ? (opts.customerId
              ? { customer: { id: opts.customerId } }
              : (opts.email ? { customer: { email: opts.email } } : {}))
            : {}),
          // Prefill customer email when available to streamline checkout.
          settings: {
            displayMode,
            variant: opts.variant || 'one-page',
            showAddDiscounts: false,
            showAddTaxId: false,
            // Prevent page navigation; use hash-only success url on the same page
            successUrl: `${currentUrl}#paddle-success`,
            ...(displayMode === 'inline'
              ? {
                  frameTarget: opts.frameTarget,
                  frameInitialHeight: opts.frameInitialHeight || '620',
                  frameStyle: opts.frameStyle || 'width: 100%; min-width: 312px; background-color: transparent; border: none;',
                }
              : {}),
            // If supported by SDK, close overlay automatically on success
            ...(typeof (window as any).Paddle?.Checkout?.open === 'function' ? {} : {}),
          },
        };

    Paddle.Checkout.open({
      ...checkoutPayload,
      onSuccess: (data: any) => {
        if (debugEnabled()) console.debug('[Paddle] Success payload', data);
        opts.onSuccess?.(data);
      },
      onError: (err: any) => {
        if (debugEnabled()) {
          try {
            const perf = (performance?.getEntriesByType?.('resource') || []) as PerformanceResourceTiming[];
            const recent = perf.filter(e => /checkout-service\.paddle\.com\/transaction-checkout/i.test(e.name)).slice(-3);
            console.groupCollapsed('[Paddle] Checkout error – diagnostics');
            console.debug('Raw error object:', err);
            // Attempt to surface common fields
            const diag: any = {
              message: err?.message || err?.error || err?.code,
              code: err?.code,
              status: err?.status || err?.statusCode,
              errors: (err as any)?.errors,
              detail: (err as any)?.detail || (err as any)?.details,
              correlationId: (err as any)?.correlationId || (err as any)?.['correlation-id'],
              ts: new Date().toISOString(),
              origin: window?.location?.origin,
              priceId: opts.priceId,
              transactionId: opts.transactionId,
            };
            console.table(diag);
            if (recent.length) {
              console.debug('Recent resource timings for transaction-checkout:', recent.map(r => ({ name: r.name, startTime: Math.round(r.startTime), duration: Math.round(r.duration), transferSize: r.transferSize })));
            }
            // Expose last error globally for quick inspection
            (window as any).__PADDLE_LAST_ERROR = { diag, err };
            console.debug('Inspect window.__PADDLE_LAST_ERROR for full payload');
            console.groupEnd();
          } catch {
            try { console.debug('[Paddle] Raw error', err); } catch {}
          }
        }
        // Normalize & enrich message for common 403 cases
        let message = err?.message || err?.error || err?.code || 'Unknown Paddle error';
        const s = String(message || '').toLowerCase();
        const status = (err && (err.status || err.statusCode)) ? Number(err.status || err.statusCode) : undefined;
        if (status === 403 || s.includes('403') || s.includes('forbidden')) {
          message = isTransactionCheckout
            ? 'Checkout blocked (403). Verify: 1) client token matches the Paddle environment, 2) saved payment methods are enabled, 3) your domain is added in Paddle Checkout Domains, 4) the transaction and saved payment method belong to the same customer.'
            : 'Checkout blocked (403). Verify: 1) client token & priceId are same environment (live/sandbox), 2) your domain (e.g., http://localhost:5173) is added in Paddle Dashboard → Settings → Checkout Settings → Checkout Domains, 3) a default checkout URL is set, 4) price is ACTIVE.';
        }
        // Specific guidance for missing default checkout URL
        const details = (err as any)?.details || (err as any)?.detail;
        if (status === 400 && (s.includes('validation') || err?.code === 'validation') && String(details || '').includes('transaction_default_checkout_url_not_set')) {
          message = 'Checkout misconfigured: set a Default checkout URL. In Paddle Dashboard (sandbox): Settings > Checkout > Checkout settings > Checkout domains / Payment links — add your dev domain(s) and set one as the default.';
        }
        opts.onError?.(new Error(message));
      },
      onClose: () => {
        if (debugEnabled()) console.debug('[Paddle] Checkout closed');
        opts.onClose?.();
      },
    });
  } catch (e:any) {
    if (debugEnabled()) console.debug('[Paddle] Exception during open', e);
    opts.onError?.(e);
  }
}

export async function closePaddleCheckout() {
  const token = import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined;
  if (!token) return;
  const Paddle = await loadPaddle(token);
  try {
    Paddle?.Checkout?.close?.();
  } catch {}
}
